import { Box, Typography, alpha, styled } from "@mui/material";

import { AssistantScenarioStatusEnum } from "../../enums";
import { AssistantActionButton } from "../../styles";
import { IAssistantScenarioStyleProps } from "./types";
import { getAssistantScenarioColors } from "./utils";

export const AssistantScenarioList = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
}));

export const AssistantScenarioButton = styled(AssistantActionButton, {
  shouldForwardProp: (prop) => prop !== "status",
})<IAssistantScenarioStyleProps>(({ theme, status }) => ({
  display: "grid",
  gridTemplateColumns: `${theme.spacing(5)} 1fr auto ${theme.spacing(2)}`,
  height: "auto",
  minHeight: theme.spacing(9),
  padding: theme.spacing(11),
  textAlign: "left",
  gap: theme.spacing(2),
  border: `1px solid ${
    status === AssistantScenarioStatusEnum.IN_PROGRESS
      ? theme.palette.secondary.main
      : theme.palette.custom.semantic.border.default
  }`,
  borderRadius: theme.shape.borderRadius[2],
  backgroundColor:
    status === AssistantScenarioStatusEnum.IN_PROGRESS
      ? alpha(theme.palette.secondary.main, theme.palette.action.focusOpacity)
      : theme.palette.custom.semantic.surface.card,
}));

export const AssistantScenarioTitle = styled("span")(({ theme }) => ({
  ...theme.typography.subtitle9,
  minWidth: 0,
  overflowWrap: "anywhere",
  color: theme.palette.primary.main,
}));

export const AssistantScenarioSymbol = styled("span", {
  shouldForwardProp: (prop) => prop !== "status",
})<IAssistantScenarioStyleProps>(({ theme, status }) => ({
  display: "grid",
  placeItems: "center",
  width: theme.spacing(5),
  height: theme.spacing(5),
  flexShrink: 0,
  borderRadius: theme.shape.borderRadius[5],
  border: `1px solid ${theme.palette.custom.semantic.border.default}`,
  ...theme.typography.subtitle9,
  color:
    status === AssistantScenarioStatusEnum.COMPLETED
      ? theme.palette.custom.forest_green
      : theme.palette.primary.main,
  backgroundColor: getAssistantScenarioColors(theme, status).symbolBackground,
}));

export const AssistantScenarioStatus = styled("span", {
  shouldForwardProp: (prop) => prop !== "status",
})<IAssistantScenarioStyleProps>(({ theme, status }) => ({
  ...theme.typography.subtitle9,
  whiteSpace: "nowrap",
  padding: theme.spacing(1, 2),
  borderRadius: theme.shape.borderRadius[5],
  color: getAssistantScenarioColors(theme, status).statusForeground,
  backgroundColor: getAssistantScenarioColors(theme, status).statusBackground,
}));

export const AssistantScenarioProgress = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(1),
}));

export const AssistantScenarioSegment = styled("span", {
  shouldForwardProp: (prop) => prop !== "status",
})<IAssistantScenarioStyleProps>(({ theme, status }) => ({
  flex: 1,
  height: theme.spacing(1),
  borderRadius: theme.shape.borderRadius[1],
  backgroundColor: getAssistantScenarioColors(theme, status).segmentBackground,
}));

export const AssistantScenarioLegend = styled(Box)(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  gap: theme.spacing(2),
}));

export const AssistantScenarioLegendItem = styled(Typography)(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: theme.spacing(1),
  color: theme.palette.custom.semantic.text.muted,
}));

export const AssistantScenarioLegendDot = styled(AssistantScenarioSegment)(
  ({ theme }) => ({
    "&&": {
      flex: "none",
      width: theme.spacing(1),
      height: theme.spacing(1),
      borderRadius: theme.shape.borderRadius[5],
    },
  })
);

export const AssistantFeatureWalkthroughSection = styled("section")(
  ({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(3),
  })
);
